import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { imageLibraryDisplayName, isLibraryDocument, IImageLibraryItem } from '../../../queries/ImageLibrary';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { DownloadIconV2 } from '../../components/svgs/icons/DownloadIconV2';
import { FileTextIcon } from '../../components/svgs/icons/FileTextIcon';
import { FolderIcon } from '../../components/svgs/icons/FolderIcon';
import { Button } from '../Button';
import { Input } from '../Input';
import { Pill } from '../Pill/Pill';
import { s } from './styles';

export interface ImageLibraryGridProps {
	items: IImageLibraryItem[];
	isLoading: boolean;
	isError?: boolean;
	onRetry?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	onLoadMore?: () => void;
	onItemClick: (item: IImageLibraryItem) => void;
	/** When provided, the caption becomes editable in place. Omit on surfaces that rename elsewhere. */
	onRename?: (item: IImageLibraryItem, displayName: string) => void;
	/** When provided, each card shows the folders it is filed under and offers to unfile it. */
	onRemoveFolder?: (item: IImageLibraryItem, folder: string) => void;
	/** When provided, each card offers to save the original file. */
	onDownload?: (item: IImageLibraryItem) => void;
	selectedIds?: Set<string>;
	emptyTitle: string;
	emptyDescription: string;
}

function ImageCaption({
	item,
	onRename,
}: {
	item: IImageLibraryItem;
	onRename?: (item: IImageLibraryItem, displayName: string) => void;
}) {
	const currentName = imageLibraryDisplayName(item);
	const [isEditing, setIsEditing] = React.useState(false);
	const [draft, setDraft] = React.useState(currentName);

	if (!onRename) {
		return <div className={css(s.cardName)}>{currentName}</div>;
	}

	const commit = () => {
		const trimmed = draft.trim();
		setIsEditing(false);
		if (trimmed && trimmed !== currentName) {
			onRename(item, trimmed);
		} else {
			setDraft(currentName);
		}
	};

	if (isEditing) {
		return (
			<div className={css(s.cardNameRow)}>
				<Input
					autoFocus
					id={`image-library-name-${item.id}`}
					onBlur={commit}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							commit();
						} else if (e.key === 'Escape') {
							setDraft(currentName);
							setIsEditing(false);
						}
					}}
					styles={[s.cardNameInput]}
					type='text'
					value={draft}
				/>
			</div>
		);
	}

	return (
		<Button
			onClick={() => {
				setDraft(currentName);
				setIsEditing(true);
			}}
			styles={[s.cardNameButton]}
			title='Rename'
			variant='ghost'
		>
			{currentName}
		</Button>
	);
}

function CardPreview({ item, onClick }: { item: IImageLibraryItem; onClick: () => void }) {
	const name = imageLibraryDisplayName(item);

	return (
		<Button aria-label={`Select ${name}`} onClick={onClick} styles={[s.cardImageButton]} variant='ghost'>
			{isLibraryDocument(item) ? (
				<span className={css(s.documentPreview)}>
					<FileTextIcon height={32} width={32} />
					<span className={css(s.documentExtension)}>{fileExtensionLabel(item)}</span>
				</span>
			) : (
				<img alt={name} className={css(s.cardImage)} loading='lazy' src={item.url} />
			)}
		</Button>
	);
}

function fileExtensionLabel(item: IImageLibraryItem) {
	const fileName = item.fileAttachment?.fileName ?? '';
	const lastDot = fileName.lastIndexOf('.');
	return lastDot > -1 ? fileName.slice(lastDot + 1).toUpperCase() : 'FILE';
}

export function ImageLibraryGrid({
	items,
	isLoading,
	isError,
	onRetry,
	hasNextPage,
	isFetchingNextPage,
	onLoadMore,
	onItemClick,
	onRename,
	onRemoveFolder,
	onDownload,
	selectedIds,
	emptyTitle,
	emptyDescription,
}: ImageLibraryGridProps) {
	if (isLoading) {
		return (
			<div className={css(s.loadingContainer)}>
				<LoadingSpinner />
			</div>
		);
	}

	// Without this an errored query looks identical to an empty library, which is a lie the user cannot act on.
	if (isError) {
		return (
			<div className={css(s.emptyState)}>
				<div className={css(s.emptyStateTitle)}>Could not load your images</div>
				<div className={css(s.emptyStateDescription)}>Something went wrong fetching the library.</div>
				{onRetry ? (
					<Button onClick={onRetry} variant='secondary'>
						Try again
					</Button>
				) : null}
			</div>
		);
	}

	if (!items.length) {
		return (
			<div className={css(s.emptyState)}>
				<div className={css(s.emptyStateTitle)}>{emptyTitle}</div>
				<div className={css(s.emptyStateDescription)}>{emptyDescription}</div>
			</div>
		);
	}

	return (
		<>
			<div className={css(s.grid)}>
				{items.map(item => (
					<div className={css(s.card, selectedIds?.has(item.id) && s.cardSelected)} key={item.id}>
						<CardPreview item={item} onClick={() => onItemClick(item)} />
						<div className={css(s.cardFooter)}>
							<ImageCaption item={item} onRename={onRename} />
							{onDownload ? (
								<Button
									aria-label={`Download ${imageLibraryDisplayName(item)}`}
									onClick={() => onDownload(item)}
									size='icon'
									title='Download'
									variant='ghost'
								>
									<DownloadIconV2 height={16} width={16} />
								</Button>
							) : null}
						</div>
						{item.keywords?.length ? (
							<div className={css(s.cardFolders)}>
								{item.keywords.map(folder => (
									<Pill
										icon={<FolderIcon fillColor='currentColor' />}
										key={folder}
										label={folder}
										onRemove={onRemoveFolder ? () => onRemoveFolder(item, folder) : undefined}
										value={folder}
										variant='keyword'
									/>
								))}
							</div>
						) : null}
					</div>
				))}
			</div>
			{hasNextPage && !isFetchingNextPage && onLoadMore && (
				<Waypoint onEnter={onLoadMore}>
					<div className={css(s.loadMoreTrigger)}>
						<LoadingSpinner />
					</div>
				</Waypoint>
			)}
		</>
	);
}
